import { useEffect, useRef } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

import type { AeroLiftingLineWakeFilament, AeroVector3 } from "../../api/aeroWorkbench";
import type { AppacFrame, AppacGeometry } from "./api";
import { triangulateCells } from "./model";

const EMPTY_WAKE_FILAMENTS: AeroLiftingLineWakeFilament[] = [];

function token(name: string, fallback: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback;
}

function geometryFrom(data: AppacGeometry): THREE.BufferGeometry {
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(data.points.flat(), 3));
  geometry.setIndex(triangulateCells(data.cells));
  geometry.computeVertexNormals();
  return geometry;
}

function scalarColors(values: number[]): Float32Array {
  const low = new THREE.Color(token("--fc-info", "steelblue"));
  const middle = new THREE.Color(token("--fc-surface", "white"));
  const high = new THREE.Color(token("--fc-danger", "crimson"));
  const limit = Math.max(1e-8, ...values.map((value) => Math.abs(value)));
  const result = new Float32Array(values.length * 3);
  values.forEach((value, index) => {
    const t = Math.min(1, Math.abs(value) / limit);
    const color = middle.clone().lerp(value < 0 ? low : high, t);
    result.set([color.r, color.g, color.b], index * 3);
  });
  return result;
}

function disposeObject(object: THREE.Object3D): void {
  object.traverse((child) => {
    const renderable = child as THREE.Object3D & {
      geometry?: THREE.BufferGeometry;
      material?: THREE.Material | THREE.Material[];
    };
    renderable.geometry?.dispose();
    if (Array.isArray(renderable.material)) renderable.material.forEach((material) => material.dispose());
    else renderable.material?.dispose();
  });
}

function finiteVector(value: AeroVector3): boolean {
  return value.length === 3 && value.every(Number.isFinite);
}

export function orientedWakeLegs(filament: AeroLiftingLineWakeFilament): Array<{
  nodes: AeroVector3[];
  circulation_m2_s: number;
}> {
  return [
    { nodes: filament.root_nodes_m, circulation_m2_s: -filament.circulation_m2_s },
    { nodes: filament.tip_nodes_m, circulation_m2_s: filament.circulation_m2_s },
  ];
}

function filamentMesh(
  nodes: AeroVector3[],
  circulation: number,
  maximumCirculation: number,
  sceneScale: number,
): THREE.Mesh | null {
  const points = nodes.filter(finiteVector).map((node) => new THREE.Vector3(...node));
  if (points.length < 2) return null;
  const ratio = Math.min(1, Math.abs(circulation) / Math.max(maximumCirculation, 1e-12));
  const curve = points.length === 2
    ? new THREE.LineCurve3(points[0]!, points[1]!)
    : new THREE.CatmullRomCurve3(points, false, "centripetal");
  const segments = Math.max(4, Math.min(160, (points.length - 1) * 4));
  const radius = Math.max(sceneScale * 0.001, 0.0015) * (0.8 + Math.sqrt(ratio) * 2.2);
  const muted = new THREE.Color(token("--fc-ink-6", "slategray"));
  const signed = new THREE.Color(token(
    circulation < 0 ? "--fc-info" : "--fc-primary-bright",
    circulation < 0 ? "steelblue" : "seagreen",
  ));
  const color = muted.lerp(signed, 0.38 + ratio * 0.62);
  const geometry = new THREE.TubeGeometry(curve, segments, radius, 6, false);
  const material = new THREE.MeshBasicMaterial({
    color,
    depthWrite: false,
    opacity: 0.48 + ratio * 0.46,
    transparent: true,
  });
  return new THREE.Mesh(geometry, material);
}

export function WakeScene({
  frame,
  previewBody = null,
  wakeFilaments = EMPTY_WAKE_FILAMENTS,
  ariaLabel = "Interactive APPAC wake viewer",
}: {
  frame: AppacFrame | null;
  previewBody?: AppacGeometry | null;
  wakeFilaments?: AeroLiftingLineWakeFilament[];
  ariaLabel?: string;
}) {
  const hostRef = useRef<HTMLDivElement>(null);
  const hasFramedBodyRef = useRef(false);
  const runtimeRef = useRef<{
    scene: THREE.Scene;
    camera: THREE.PerspectiveCamera;
    renderer: THREE.WebGLRenderer;
    controls: OrbitControls;
    group: THREE.Group;
    observer: ResizeObserver;
    raf: number;
  } | null>(null);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(token("--fc-surface-2", "whitesmoke"));
    const camera = new THREE.PerspectiveCamera(36, 1, 0.001, 10_000);
    camera.up.set(0, 0, 1);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    host.appendChild(renderer.domElement);
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.08;
    const group = new THREE.Group();
    scene.add(group);
    scene.add(new THREE.HemisphereLight(
      token("--fc-surface", "white"),
      token("--fc-ink-4", "gray"),
      2.2,
    ));
    const key = new THREE.DirectionalLight(token("--fc-surface", "white"), 2.5);
    key.position.set(-2, -3, 5);
    scene.add(key);

    let raf = 0;
    const render = () => {
      controls.update();
      renderer.render(scene, camera);
      raf = requestAnimationFrame(render);
    };
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      renderer.setSize(Math.max(1, width), Math.max(1, height), false);
      camera.aspect = Math.max(1, width) / Math.max(1, height);
      camera.updateProjectionMatrix();
    });
    observer.observe(host);
    render();
    runtimeRef.current = { scene, camera, renderer, controls, group, observer, raf };
    return () => {
      cancelAnimationFrame(raf);
      observer.disconnect();
      controls.dispose();
      for (const child of [...group.children]) {
        group.remove(child);
        disposeObject(child);
      }
      renderer.dispose();
      host.replaceChildren();
      runtimeRef.current = null;
      hasFramedBodyRef.current = false;
    };
  }, []);

  useEffect(() => {
    const runtime = runtimeRef.current;
    if (!runtime) return;
    const { group, camera, controls } = runtime;
    for (const child of [...group.children]) {
      group.remove(child);
      disposeObject(child);
    }

    const bodyData = frame?.body ?? previewBody;
    let bodyBounds: THREE.Box3 | null = null;
    let bodyScale = 1;
    if (bodyData) {
      const bodyGeometry = geometryFrom(bodyData);
      if (bodyData.scalars?.length === bodyData.points.length) {
        bodyGeometry.setAttribute("color", new THREE.BufferAttribute(scalarColors(bodyData.scalars), 3));
      }
      const hasScalars = Boolean(bodyData.scalars);
      const bodyMaterial = hasScalars
        ? new THREE.MeshStandardMaterial({
          vertexColors: true,
          metalness: 0.04,
          roughness: 0.72,
          side: THREE.DoubleSide,
        })
        : new THREE.MeshBasicMaterial({
          color: token("--fc-primary-bright", "#1aaf6b"),
          opacity: 0.72,
          side: THREE.DoubleSide,
          transparent: true,
        });
      const body = new THREE.Mesh(
        bodyGeometry,
        bodyMaterial,
      );
      group.add(body);
      bodyBounds = new THREE.Box3().setFromObject(body);
      if (!bodyBounds.isEmpty()) {
        const size = bodyBounds.getSize(new THREE.Vector3());
        bodyScale = Math.max(size.x, size.y, size.z, 0.1);
      }
    }

    if (frame && frame.points.length > 0 && frame.cells.length > 0) {
      const wake = new THREE.Mesh(
        geometryFrom(frame),
        new THREE.MeshBasicMaterial({
          color: token("--fc-primary", "seagreen"),
          opacity: 0.5,
          transparent: true,
          wireframe: true,
          side: THREE.DoubleSide,
        }),
      );
      group.add(wake);
    }

    const maximumCirculation = Math.max(
      1e-12,
      ...wakeFilaments.map((filament) => Math.abs(filament.circulation_m2_s)),
    );
    wakeFilaments.forEach((filament) => {
      // Match the backend kernel's oriented horseshoe convention: the root
      // trailing leg carries -Γ and the tip trailing leg carries +Γ.
      orientedWakeLegs(filament).forEach((leg) => {
        const mesh = filamentMesh(
          leg.nodes,
          leg.circulation_m2_s,
          maximumCirculation,
          bodyScale,
        );
        if (mesh) group.add(mesh);
      });
    });

    // The user's view is part of the design state. Frame it from the wing body
    // only on first load; geometry refreshes and free-wake solves must not move it.
    if (!hasFramedBodyRef.current && bodyBounds && !bodyBounds.isEmpty()) {
      const center = bodyBounds.getCenter(new THREE.Vector3());
      const size = bodyBounds.getSize(new THREE.Vector3());
      const radius = Math.max(size.x, size.y, size.z, 0.1);
      controls.target.copy(center);
      camera.position.set(center.x + radius * 0.85, center.y - radius * 1.35, center.z + radius * 0.7);
      camera.near = radius / 1000;
      camera.far = radius * 100;
      camera.updateProjectionMatrix();
      controls.update();
      hasFramedBodyRef.current = true;
    }
  }, [frame, previewBody, wakeFilaments]);

  return (
    <div
      className="appac-wake-scene"
      ref={hostRef}
      aria-label={ariaLabel}
      data-wake-filament-count={wakeFilaments.length}
    />
  );
}
